import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  api,
  FileRefused,
  type DateEvidence,
  type EvaluationDetail,
  type EvaluationRun,
  type HoldingsReading,
  type Reproduction,
  type SnapshotDetail,
  type SnapshotSummary,
} from '../api'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import {
  DateFilterHeader,
  EVERY_DATE,
  FilterHeader,
  SortHeader,
  useSort,
  type DateRange,
} from '../components/Grid'
import { RowMenu } from '../components/RowMenu'
import { RunVerdicts } from '../components/RunVerdicts'
import { FundPicker, useFunds } from '../components/FundPicker'
import { DeskCell, DeskHeader, useDeskColumns } from '../desks'

/**
 * Holdings snapshots — TECHNICAL_DESIGN.md §7.2, the Snapshot Evaluator pack.
 *
 * Two screens over one loader. **Load a snapshot** reads a file, proposes what each
 * column holds, and loads it only once a person has seen the mapping: a column read
 * as the wrong thing loads without complaint, so the mapping is what is on screen,
 * with sample values beside every column. **Holdings snapshots** lists what has
 * been loaded, each with the date it was as at, which every verdict from it carries.
 *
 * A refused file shows every problem, row by row. The loader refuses a file whole
 * rather than loading the rows it could read, so the list is the whole correction.
 */

const TARGET_GROUPS: { id: string; label: string }[] = [
  { id: 'position', label: 'Position' },
  { id: 'fund', label: 'Fund and date' },
  { id: 'figure', label: 'Fund figures' },
  { id: 'classification', label: 'Classification' },
  { id: 'rating', label: 'Rating' },
  { id: 'analytic', label: 'Risk analytics' },
]

const COLUMN_LABELS: Record<string, string> = {
  security_id: 'Security',
  market_value: 'Market value',
  issuer_id: 'Issuer',
  exposure: 'Exposure',
  duration: 'Duration',
  spread_duration: 'Spread duration',
  maturity_date: 'Maturity',
  reset_date: 'Next reset',
  quantity: 'Quantity',
  issuer_shares_outstanding: 'Issuer shares',
  in_default: 'In default',
  in_benchmark: 'In benchmark',
}

const FIGURE_LABELS: Record<string, string> = {
  nav: 'NAV',
  net_assets: 'Net assets',
  total_assets: 'Total assets',
}

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function columnLabel(column: string): string {
  if (column.startsWith('class:')) return column.slice('class:'.length).replace(/_/g, ' ')
  if (column.startsWith('rating:')) return `${column.slice('rating:'.length)} rating`
  return COLUMN_LABELS[column] ?? column
}

function amount(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value)
}

/** The day the holdings were as at, as the file stated it — not moved into the
 *  reader's time zone, where a date at midnight UTC becomes the day before. */
function asAt(instant: string): string {
  return new Date(instant).toISOString().slice(0, 10)
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'number') return amount(value)
  return String(value)
}

function PackNote({ children }: { children: ReactNode }) {
  return (
    <p className="note">
      {children} Part of the <EvaluatorName strong={false} />.
      <AddOnFootnote />
    </p>
  )
}

export function Holdings({
  mode,
  onLoaded,
  focus,
}: {
  mode: 'list' | 'load'
  onLoaded: () => void
  /** A snapshot to open on arriving, as when Scheduled Jobs opens one a run loaded. */
  focus?: string
}) {
  return mode === 'load' ? <LoadSnapshot onLoaded={onLoaded} /> : <SnapshotList focus={focus} />
}

/** The instant a day starts, in the reader's own time zone. */
function dayStarts(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString()
}

/** The instant after a day ends, in the reader's own time zone. */
function dayEnds(day: string): string {
  const next = new Date(`${day}T00:00:00`)
  next.setDate(next.getDate() + 1)
  return next.toISOString()
}

function SnapshotList({ focus }: { focus?: string }) {
  const { funds } = useFunds()
  const [fund, setFund] = useState('')
  const [asOf, setAsOf] = useState<DateRange>(EVERY_DATE)
  const [loadedOn, setLoadedOn] = useState<DateRange>(EVERY_DATE)
  const { sort, onSort } = useSort()
  /** The desk the list is narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const deskColumns = useDeskColumns()
  /** A page of snapshots, fetched a page at a time so the list stays quick as snapshots accumulate. */
  const [found, setFound] = useState<Awaited<ReturnType<typeof api.snapshots>> | null>(null)
  // A change of filter or order starts again from the first page.
  const filters = JSON.stringify([fund, asOf, loadedOn, sort, desk])
  const [page, setPage] = useState({ filters, offset: 0, limit: 50 })
  const offset = page.filters === filters ? page.offset : 0
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<SnapshotSummary | null>(null)
  const filtered = Boolean(fund || desk || asOf.from || asOf.to || loadedOn.from || loadedOn.to)

  useEffect(() => {
    if (!focus) return
    let live = true
    api
      .snapshot(focus, { limit: 1 })
      .then((found) => live && setOpen(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [focus])

  // The rows already shown stay while the next set is fetched: emptying the table
  // on every change of filter would close the filter being set.
  useEffect(() => {
    let live = true
    setError(null)
    api
      .snapshots({
        portfolioId: fund || undefined,
        asOfFrom: asOf.from || undefined,
        asOfTo: asOf.to || undefined,
        loadedFrom: loadedOn.from ? dayStarts(loadedOn.from) : undefined,
        loadedBefore: loadedOn.to ? dayEnds(loadedOn.to) : undefined,
        sort: sort?.key,
        order: sort?.direction,
        desk: desk || undefined,
        offset,
        limit: page.limit,
      })
      .then((listed) => live && setFound(listed))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [fund, asOf, loadedOn, sort, offset, page.limit])

  function clearFilters() {
    setFund('')
    setDesk('')
    setAsOf(EVERY_DATE)
    setLoadedOn(EVERY_DATE)
  }

  // A snapshot opens in place of the list rather than under it: the list can be long,
  // and a card below it was a scroll away from the row that opened it.
  if (open) return <SnapshotView summary={open} onBack={() => setOpen(null)} />

  return (
    <section className="holdings">
      <header className="page-head">
        <h2>Holdings snapshots</h2>
        <PackNote>
          The holdings each verdict is judged against, exactly as loaded and with the
          date they were as at.
        </PackNote>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {found === null ? (
        <p className="loading">Loading…</p>
      ) : found.total === 0 && !filtered ? (
        <p className="empty" data-testid="no-snapshots">
          No snapshots loaded yet. Load one under Evaluation, Load a snapshot.
        </p>
      ) : (
        <>
        <div className="table-scroll">
          {filtered && (
            <p className="showing" data-testid="showing">
              {`${found.total} ${found.total === 1 ? 'snapshot matches' : 'snapshots match'}.`}
              {filtered && (
                <button type="button" className="link" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </p>
          )}
          <table className="grid stacked" data-testid="snapshots">
            <thead>
              <tr>
                <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                <FilterHeader
                  label="Fund"
                  sortKey="fund"
                  sort={sort}
                  onSort={onSort}
                  value={fund}
                  onChange={setFund}
                  all="Every fund"
                  testId="filter-fund"
                  options={funds.map((f) => ({ value: f.id, label: f.code }))}
                />
                <DateFilterHeader
                  label="As at"
                  sortKey="as_of"
                  sort={sort}
                  onSort={onSort}
                  value={asOf}
                  onChange={setAsOf}
                  all="Every as-at date"
                  testId="filter-as-at"
                />
                <SortHeader label="Positions" sortKey="positions" sort={sort} onSort={onSort} className="num" />
                <SortHeader label="Market value" sortKey="market_value" sort={sort} onSort={onSort} className="num" />
                <SortHeader label="NAV" sortKey="nav" sort={sort} onSort={onSort} className="num" />
                <DateFilterHeader
                  label="Loaded"
                  sortKey="loaded"
                  sort={sort}
                  onSort={onSort}
                  value={loadedOn}
                  onChange={setLoadedOn}
                  all="Any day"
                  testId="filter-loaded"
                />
              </tr>
            </thead>
            <tbody>
              {found.snapshots.length === 0 && (
                <tr>
                  <td colSpan={6 + deskColumns} className="empty" data-testid="no-match">
                    No snapshot matches these filters.
                  </td>
                </tr>
              )}
              {found.snapshots.map((s) => (
                <tr key={s.id} className="clickable" title={`Loaded from ${s.filename}`} onClick={() => setOpen(s)}>
                  <DeskCell fund={s.portfolio_code} />
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpen(s)
                      }}
                    >
                      {s.portfolio_code}
                    </button>
                    {s.portfolio_name !== s.portfolio_code && (
                      <span className="clause">{s.portfolio_name}</span>
                    )}
                  </td>
                  <td>{asAt(s.as_of)}</td>
                  <td className="num">{s.position_count}</td>
                  <td className="num">
                    {amount(s.total_market_value)} {s.base_currency}
                  </td>
                  <td className="num">{amount(s.figures.nav)}</td>
                  <td>
                    {new Date(s.loaded_at).toLocaleDateString()}
                    {s.loaded_by && <span className="clause">{s.loaded_by}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {found.total > 0 && (
          <div className="snapshot-pager" data-testid="snapshots-pager">
            <span>{`Snapshots ${offset + 1}–${Math.min(offset + page.limit, found.total)} of ${found.total}`}</span>
            <label>
              Snapshots per page
              <select
                value={page.limit}
                onChange={(e) => setPage({ filters, offset: 0, limit: Number(e.target.value) })}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="ghost"
              disabled={offset === 0}
              onClick={() => setPage({ filters, offset: Math.max(0, offset - page.limit), limit: page.limit })}
            >
              Previous
            </button>
            <button
              type="button"
              className="ghost"
              disabled={offset + page.limit >= found.total}
              onClick={() => setPage({ filters, offset: offset + page.limit, limit: page.limit })}
            >
              Next
            </button>
          </div>
        )}
        </>
      )}
    </section>
  )
}

const VERDICT_STATES = ['breach', 'not_evaluable', 'warn', 'pass'] as const

/**
 * The evaluations of one snapshot — §6.2, proposal §5.5.
 *
 * Advisory. A run judges the snapshot against its fund's limits as they applied on
 * the snapshot's date, as recorded at the moment of the run, and keeps everything
 * it was given — so *Rerun* runs it again from that record and says whether the verdicts
 * come out the same. Not evaluable is counted beside breach, never below warn
 * (§6.4): a limit that could not be judged has not been passed.
 */
function EvaluationsPanel({ snapshot }: { snapshot: SnapshotSummary }) {
  const [runs, setRuns] = useState<EvaluationRun[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checks, setChecks] = useState<Record<string, Reproduction | 'checking'>>({})
  /** The run whose verdicts are open, and the one being fetched. */
  const [open, setOpen] = useState<EvaluationDetail | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  async function view(id: string) {
    setOpening(id)
    setError(null)
    try {
      setOpen(await api.evaluation(id))
    } catch (e) {
      setError(said(e))
    } finally {
      setOpening(null)
    }
  }

  useEffect(() => {
    let live = true
    api
      .evaluations({ snapshotId: snapshot.id })
      .then((found) => live && setRuns(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [snapshot.id])

  async function evaluate() {
    setBusy(true)
    setError(null)
    try {
      const run = await api.evaluate(snapshot.id)
      // The new run is the fund's latest for the day, so none before it is checked.
      setRuns((was) => [run, ...(was ?? []).map((r) => ({ ...r, latest: false }))])
      // What it came to, straight away: the counts alone say how many, not which.
      void view(run.id)
    } catch (e) {
      setError(said(e))
    } finally {
      setBusy(false)
    }
  }

  async function check(id: string) {
    setChecks((was) => ({ ...was, [id]: 'checking' }))
    try {
      const result = await api.reproduceEvaluation(id)
      setChecks((was) => ({ ...was, [id]: result }))
    } catch (e) {
      setError(said(e))
      setChecks((was) => {
        const next = { ...was }
        delete next[id]
        return next
      })
    }
  }

  return (
    <div className="card evaluations" data-testid="evaluations">
      <div className="evaluations-head">
        <h3>Evaluations</h3>
        <button type="button" disabled={busy} onClick={() => void evaluate()}>
          {busy ? 'Evaluating…' : 'Evaluate against the limits'}
        </button>
      </div>
      <p className="note">
        Advisory. Each run judges these holdings against the limits that applied on{' '}
        {asAt(snapshot.as_of)}, and keeps everything it was given so it can be rerun later.
        Text only counts the limits written as words, which a person reads.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {runs === null ? (
        <p className="loading">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="empty">Not evaluated yet.</p>
      ) : (
        <div className="table-scroll">
          {!runs.some((run) => run.latest) && (
            <p className="note" data-testid="later-snapshot">
              {snapshot.portfolio_code} as at {asAt(snapshot.as_of)} has been evaluated since, from
              another snapshot of the same day. That run is the one to rerun.
            </p>
          )}
          <table className="grid stacked" data-testid="runs">
            <thead>
              <tr>
                <th>Ran</th>
                <th className="num">Breach</th>
                <th className="num">Not evaluable</th>
                <th className="num">Warn</th>
                <th className="num">Pass</th>
                <th className="num">Text only</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const checked = checks[run.id]
                return (
                  <tr
                    key={run.id}
                    data-testid="run"
                    className={`clickable${open?.id === run.id ? ' selected' : ''}`}
                    onClick={() => (open?.id === run.id ? setOpen(null) : void view(run.id))}
                  >
                    <td>
                      {new Date(run.ran_at).toLocaleString()}
                      {(() => {
                        // The fund's limits, reconciled: those judged, and the rating
                        // policies they were read through, which have no verdict of their own.
                        const judged = Object.values(run.counts).reduce((a, b) => a + b, 0)
                        const policies = run.rule_count - judged
                        return (
                          <span className="clause" data-testid="run-limits">
                            {judged} judged
                            {policies > 0 && ` · ${policies} rating polic${policies === 1 ? 'y' : 'ies'} applied`}
                            {run.run_by ? ` · ${run.run_by}` : ''}
                          </span>
                        )
                      })()}
                      {/* What a check found, under the run it is about, rather than a
                          column that was empty until somebody asked. */}
                      {!run.latest && checked === undefined && (
                        <span className="clause" data-testid="superseded">
                          A later run stands
                        </span>
                      )}
                      {checked === 'checking' && <span className="clause">Rerunning…</span>}
                      {checked !== undefined && checked !== 'checking' && checked.reproduces && (
                        <span className="clause check-same" data-testid="reproduces">
                          Rerun: same verdicts
                        </span>
                      )}
                      {checked !== undefined && checked !== 'checking' && !checked.reproduces && (
                        <span className="clause check-differs" data-testid="differs">
                          {checked.differences.length > 0
                            ? `Rerun differs on ${checked.differences.map((d) => d.rule_key).join(', ')}`
                            : 'Rerun: its inputs no longer match'}
                        </span>
                      )}
                    </td>
                    {VERDICT_STATES.map((state) => {
                      // Wording is counted on its own: a limit written as words was never
                      // going to be checked against holdings, and is not one that failed to be.
                      const n = state === 'not_evaluable' ? run.counts[state] - (run.text_only ?? 0) : run.counts[state]
                      return (
                        <td key={state} className="num">
                          <span className={`verdict-count ${state}${n === 0 ? ' zero' : ''}`}>{n}</span>
                        </td>
                      )
                    })}
                    <td className="num">
                      <span className="verdict-count text-only" data-testid="text-only">
                        {run.text_only ?? 0}
                      </span>
                    </td>
                    {/* The menu's own clicks stay in the menu; the row is a toggle. */}
                    <td className="run-actions" onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        label={`Actions for the run at ${new Date(run.ran_at).toLocaleString()}`}
                        testId="run-menu"
                        items={[
                          {
                            label: open?.id === run.id ? 'Close verdicts' : 'Verdicts',
                            disabled: opening === run.id,
                            onSelect: () => (open?.id === run.id ? setOpen(null) : void view(run.id)),
                          },
                          {
                            label: 'Rerun',
                            disabled: !run.latest || checked === 'checking',
                            why: !run.latest
                              ? 'A later run of this fund on this day stands. Rerun that one.'
                              : undefined,
                            onSelect: () => void check(run.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {open && <RunVerdicts detail={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

const SNAPSHOT_TABS = [
  ['summary', 'Summary'],
  ['details', 'Details'],
  ['evaluations', 'Evaluations'],
] as const

const PAGE_SIZES = [25, 50, 100]
/** Shown unless every column is asked for. Classifications always are, and the
 *  agencies' ratings share one column. */
const KEY_COLUMNS = ['security_id', 'issuer_id', 'market_value', 'duration', 'maturity_date']

/**
 * One snapshot: its figures as tiles, and its positions a page at a time.
 *
 * A book of several hundred positions with a dozen columns was one very wide, very
 * long table. Now the key columns show by default, with every column a switch
 * away; the header stays in view while the rows scroll; a search finds a security
 * or an issuer across the whole snapshot, not only the page on screen; and the
 * whole snapshot exports as CSV or Excel.
 */
function SnapshotView({ summary, onBack }: { summary: SnapshotSummary; onBack: () => void }) {
  const [page, setPage] = useState({ offset: 0, limit: 50 })
  const [find, setFind] = useState('')
  const [searching, setSearching] = useState('')
  const [detail, setDetail] = useState<SnapshotDetail | null>(null)
  const [everyColumn, setEveryColumn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [tab, setTab] = useState<(typeof SNAPSHOT_TABS)[number][0]>('summary')

  // A search waits for typing to pause, and starts from the first page.
  useEffect(() => {
    const pause = window.setTimeout(() => {
      setSearching(find.trim())
      setPage((was) => ({ ...was, offset: 0 }))
    }, 300)
    return () => window.clearTimeout(pause)
  }, [find])

  useEffect(() => {
    let live = true
    api
      .snapshot(summary.id, { offset: page.offset, limit: page.limit, find: searching || undefined })
      .then((found) => live && setDetail(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [summary.id, page, searching])

  async function exportAs(format: 'csv' | 'xlsx') {
    setExporting(true)
    setError(null)
    try {
      await api.downloadSnapshot(summary.id, format)
    } catch (e) {
      setError(said(e))
    } finally {
      setExporting(false)
    }
  }

  const s = detail ?? summary
  const columns = detail?.columns ?? summary.columns
  const ratingColumns = columns.filter((c) => c.startsWith('rating:'))
  const shown = everyColumn
    ? columns
    : [
        ...KEY_COLUMNS.filter((c) => columns.includes(c)),
        ...columns.filter((c) => c.startsWith('class:')),
      ]
  const nav = s.figures.nav
  const total = detail?.positions_total ?? 0
  const first = total === 0 ? 0 : page.offset + 1
  const last = Math.min(page.offset + page.limit, total)

  return (
    <section className="holdings" data-testid="snapshot-detail">
      <header className="page-head snapshot-head">
        <button type="button" className="ghost" onClick={onBack}>
          ← All snapshots
        </button>
        <h2>
          {s.portfolio_code} as at {asAt(s.as_of)}
        </h2>
        <p className="note">
          {s.portfolio_name !== s.portfolio_code ? `${s.portfolio_name} · ` : ''}loaded from{' '}
          {s.filename}
          {s.profile_name ? ` with the mapping “${s.profile_name}”` : ''} on{' '}
          {new Date(s.loaded_at).toLocaleString()}
          {s.loaded_by ? ` by ${s.loaded_by}` : ''}.
        </p>
      </header>

      {/* Three views of one snapshot rather than one long page: what it is, what it
          holds, and what it came to against the limits. */}
      <div className="tabstrip sub snapshot-tabs" role="tablist" aria-label="Snapshot">
        {SNAPSHOT_TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {tab === 'summary' && (
      <>
      <div className="snapshot-tiles" data-testid="snapshot-tiles">
        <div className="snapshot-tile">
          <span>Positions</span>
          <strong>{s.position_count}</strong>
          {s.declared_count !== null && <small>matches the count the file states</small>}
        </div>
        <div className="snapshot-tile">
          <span>Market value</span>
          <strong>
            {amount(s.total_market_value)} {s.base_currency}
          </strong>
        </div>
        {Object.entries(s.figures).map(([code, value]) => (
          <div className="snapshot-tile" key={code}>
            <span>{FIGURE_LABELS[code] ?? code}</span>
            <strong>
              {amount(value)} {s.base_currency}
            </strong>
          </div>
        ))}
        {Object.entries(s.analytics).map(([code, value]) => (
          <div className="snapshot-tile" key={code}>
            <span>{code.replace(/_/g, ' ')}</span>
            <strong>{amount(value)}</strong>
          </div>
        ))}
      </div>

      {s.notes.length > 0 && (
        <ul className="snapshot-notes">
          {s.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      </>
      )}

      {tab === 'evaluations' && <EvaluationsPanel snapshot={s} />}

      {tab === 'details' && s.positions_removed && (
        <p className="note" data-testid="positions-removed">
          This snapshot’s positions were removed under the retention policy. Its figures, and every
          evaluation of it still kept, remain.
        </p>
      )}
      {tab === 'details' && !s.positions_removed && (
      <div className="card">
        <div className="snapshot-toolbar">
          <input
            type="search"
            aria-label="Find a security or issuer"
            placeholder="Find a security or issuer"
            value={find}
            onChange={(e) => setFind(e.target.value)}
          />
          <label className="checkbox">
            <input
              type="checkbox"
              checked={everyColumn}
              onChange={(e) => setEveryColumn(e.target.checked)}
            />
            Show every column
          </label>
          <span className="spacer" />
          <RowMenu
            label="Export"
            text="Export"
            disabled={exporting}
            items={[
              { label: 'CSV', onSelect: () => void exportAs('csv') },
              { label: 'Excel', onSelect: () => void exportAs('xlsx') },
            ]}
          />
        </div>

        <div className="table-scroll snapshot-positions">
          <table className="grid" data-testid="positions">
            <thead>
              <tr>
                <th className="num">Row</th>
                {shown.map((column) => (
                  <th key={column} className={column === 'market_value' ? 'num' : undefined}>
                    {columnLabel(column)}
                  </th>
                ))}
                {!everyColumn && nav ? <th className="num">% of NAV</th> : null}
                {!everyColumn && ratingColumns.length > 0 && (
                  <th>Ratings ({ratingColumns.map((c) => c.slice('rating:'.length)).join(' / ')})</th>
                )}
              </tr>
            </thead>
            <tbody>
              {(detail?.positions ?? []).map((position) => (
                <tr key={String(position.line)}>
                  <td className="num">{String(position.line)}</td>
                  {shown.map((column) => (
                    <td
                      key={column}
                      className={typeof position[column] === 'number' ? 'num' : undefined}
                    >
                      {cell(position[column])}
                    </td>
                  ))}
                  {!everyColumn && nav ? (
                    <td className="num">
                      {typeof position.market_value === 'number'
                        ? ((position.market_value / nav) * 100).toFixed(2)
                        : ''}
                    </td>
                  ) : null}
                  {!everyColumn && ratingColumns.length > 0 && (
                    <td>{ratingColumns.map((c) => cell(position[c]) || '–').join(' / ')}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {detail && total === 0 && (
            <p className="empty">No position matches “{searching}”.</p>
          )}
        </div>

        <div className="snapshot-pager" data-testid="pager">
          <span>
            {detail ? `Rows ${first}–${last} of ${total}` : 'Loading…'}
            {searching ? ` matching “${searching}”` : ''}
          </span>
          <label>
            Rows per page
            <select
              value={page.limit}
              onChange={(e) => setPage({ offset: 0, limit: Number(e.target.value) })}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ghost"
            disabled={page.offset === 0}
            onClick={() => setPage({ ...page, offset: Math.max(0, page.offset - page.limit) })}
          >
            Previous
          </button>
          <button
            type="button"
            className="ghost"
            disabled={last >= total}
            onClick={() => setPage({ ...page, offset: page.offset + page.limit })}
          >
            Next
          </button>
        </div>
      </div>
      )}
    </section>
  )
}

/**
 * The file, and the mapping to read it with, at the top; the outcome directly under
 * them. A refusal used to arrive below the Load button at the foot of a long page,
 * where somebody who had just pressed the button — and was looking at it — did not
 * see it. The page now scrolls back to the outcome, and the button sits in a bar
 * that stays in view and says what it is still waiting for.
 */
function LoadSnapshot({ onLoaded }: { onLoaded: () => void }) {
  const { funds } = useFunds()
  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState<HoldingsReading | null>(null)
  const [headerRow, setHeaderRow] = useState(1)
  const [columns, setColumns] = useState<Record<string, string>>({})
  const [dateOrder, setDateOrder] = useState('')
  const [fund, setFund] = useState('')
  const [asOf, setAsOf] = useState('')
  const [figures, setFigures] = useState<Record<string, string>>({})
  const [save, setSave] = useState(false)
  const [saveAs, setSaveAs] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [loaded, setLoaded] = useState<SnapshotSummary[] | null>(null)
  const outcome = useRef<HTMLDivElement>(null)
  // Bumped to clear the file input when starting again after a load.
  const [fileKey, setFileKey] = useState(0)
  // What was filled in from the file's name, so the screen can say so while it stands.
  const [prefilled, setPrefilled] = useState<{ fund: string | null; asOf: string | null }>({
    fund: null,
    asOf: null,
  })

  /** Back to an empty screen, for the next file. */
  function startAgain() {
    setFile(null)
    setReading(null)
    setColumns({})
    setDateOrder('')
    setFund('')
    setAsOf('')
    setFigures({})
    setSave(false)
    setSaveAs('')
    setError(null)
    setProblems([])
    setLoaded(null)
    setPrefilled({ fund: null, asOf: null })
    setFileKey((key) => key + 1)
  }

  // Bring the outcome into view whenever there is a new one.
  useEffect(() => {
    if (!error && problems.length === 0 && !loaded) return
    outcome.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    outcome.current?.focus({ preventScroll: true })
  }, [error, problems, loaded])

  /**
   * Read a file. `fresh` is a newly chosen file: nothing typed for the last one
   * carries over, since a fund, a date or a NAV entered for HALVEN's file is wrong
   * for the next one. Re-reading the same file from another sheet or header row
   * keeps what was entered.
   */
  async function readFile(chosen: File, sheet?: string, row?: number, fresh = false) {
    setBusy(true)
    setError(null)
    setProblems([])
    setLoaded(null)
    if (fresh) {
      setReading(null)
      setColumns({})
      setDateOrder('')
      setFund('')
      setAsOf('')
      setFigures({})
      setSave(false)
      setSaveAs('')
      setPrefilled({ fund: null, asOf: null })
    }
    try {
      const next = await api.readHoldings(chosen, sheet, row)
      setReading(next)
      setHeaderRow(next.header_row + 1)
      setColumns(next.columns)
      setDateOrder(next.date_order ?? '')
      // The fund and the day from the file's name, into empty fields only: anything
      // the person has already chosen for this file stands.
      const named = next.from_filename
      const fundFromName = (fresh || !fund) && named.portfolio_id ? named.portfolio_id : null
      const asOfFromName = (fresh || !asOf) && named.as_of ? named.as_of : null
      if (fundFromName) setFund(fundFromName)
      if (asOfFromName) setAsOf(asOfFromName)
      setPrefilled({ fund: fundFromName, asOf: asOfFromName })
      // A new layout is worth saving; one that came from a saved mapping already is.
      // Named after the file it came from, which is how the person loading it knows
      // it. The screen never presents the name as the fund's.
      setSave(next.source !== 'profile' || (next.added ?? []).length > 0)
      setSaveAs(next.profile?.name ?? chosen.name.replace(/\.[^.]+$/, ''))
    } catch (e) {
      setReading(null)
      setError(said(e))
    } finally {
      setBusy(false)
    }
  }

  /** One column holds one thing, and one thing comes from one column. */
  function assign(header: string, target: string) {
    setColumns((was) => {
      const next: Record<string, string> = {}
      for (const [t, h] of Object.entries(was)) {
        if (h !== header && t !== target) next[t] = h
      }
      if (target) next[target] = header
      return next
    })
  }

  const targetOf = (header: string) =>
    Object.entries(columns).find(([, h]) => h === header)?.[0] ?? ''
  const kinds = new Map((reading?.targets ?? []).map((t) => [t.target, t.kind]))
  const missing = (reading?.targets ?? []).filter((t) => t.required && !columns[t.target])
  const fundColumn = Boolean(columns.portfolio_code)
  const asOfColumn = Boolean(columns.as_of)
  const figuresMapped = Object.keys(columns).some((t) => t.startsWith('figure:'))
  // How dates written 03/04/2026 are read. Wherever one of the file's own dates can
  // only be read one way (15/08/2036 has no fifteenth month), that settles it; the
  // person is asked only when no date in the mapped date columns can decide.
  const evidence = Object.entries(columns)
    .filter(([target]) => kinds.get(target) === 'date')
    .map(([, header]) => reading?.date_evidence[header])
    .filter((found): found is DateEvidence => Boolean(found))
  const dayProof = evidence.find((e) => e.day_first)?.day_first ?? null
  const monthProof = evidence.find((e) => e.month_first)?.month_first ?? null
  const datesConflict = Boolean(dayProof && monthProof)
  const detected = datesConflict ? null : dayProof ? 'dmy' : monthProof ? 'mdy' : null
  const datesUndecided = !detected && !datesConflict && evidence.some((e) => e.ambiguous)
  const waitingFor = [
    !reading && 'choose a file',
    missing.length > 0 && `map ${missing.map((t) => t.label.toLowerCase()).join(' and ')}`,
    reading && !fundColumn && !fund && 'choose the fund',
    reading && !asOfColumn && !asOf && 'set the as-at date',
    datesConflict && 'correct the dates the file writes both ways',
    datesUndecided && !dateOrder && 'say whether the dates put the day or the month first',
    reading && save && !saveAs.trim() && 'name the mapping',
  ].filter((reason): reason is string => Boolean(reason))
  const ready = Boolean(file && reading && waitingFor.length === 0)

  async function submit() {
    if (!reading || !file) return
    setBusy(true)
    setError(null)
    setProblems([])
    setLoaded(null)
    const typed = Object.fromEntries(Object.entries(figures).filter(([, v]) => v.trim()))
    try {
      const result = await api.loadSnapshot(file, {
        mapping: {
          sheet: reading.sheet,
          header_row: reading.header_row,
          columns,
          date_order: detected ?? (datesUndecided ? dateOrder || null : null),
        },
        // A date typed or filled in only stands where the file has no as-of column
        // of its own; the column is what the snapshot is dated by otherwise.
        as_of: asOfColumn ? undefined : asOf || undefined,
        portfolio_id: fundColumn ? undefined : fund,
        figures: fundColumn ? undefined : typed,
        save_profile_as: save ? saveAs.trim() || undefined : undefined,
      })
      setLoaded(result.snapshots)
    } catch (e) {
      setError(said(e))
      if (e instanceof FileRefused) setProblems(e.problems)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="holdings-load">
      <header className="page-head">
        <h2>Load a snapshot</h2>
        <PackNote>
          A holdings file from your order management system, custodian or fund
          administrator, read through a mapping you confirm and kept exactly as loaded.
        </PackNote>
      </header>

      <div className="card load-source">
        <label className="load-file">
          Holdings file (.csv or .xlsx)
          <input
            key={fileKey}
            type="file"
            accept=".csv,.xlsx,.xlsm"
            onChange={(e) => {
              const chosen = e.target.files?.[0] ?? null
              setFile(chosen)
              // A different file starts the screen over, from that file alone.
              if (chosen) void readFile(chosen, undefined, undefined, true)
              else startAgain()
            }}
          />
        </label>
        {/* Once loaded there is no mapping left to decide, and the success panel
            below says what happened to it. */}
        {!loaded && (
        <div className="load-mapping">
          {reading?.source === 'profile' ? (
            // Named, and said to be shared: the mapping belongs to the column headings,
            // so the one saved from BRAETON's file is the one HALVEN's file is read by.
            <p className="note" data-testid="saved-mapping">
              Columns mapped by the saved mapping <strong>{reading.profile?.name}</strong>, used for
              every file with these {reading.headers.filter((h) => h.trim()).length} column
              headings, whichever fund it is for. The fund and the date for this file are set below.
            </p>
          ) : null}
          {reading?.source === 'profile' && (reading.added ?? []).length > 0 && (
            <p className="note" data-testid="mapping-topped-up">
              The saved mapping leaves out{' '}
              {reading
                .added!.map((target) => {
                  const label = reading.targets.find((t) => t.target === target)?.label ?? target
                  return `“${reading.columns[target]}” as ${label.replace(/^(Classification|Rating): /, '').toLowerCase()}`
                })
                .join(', ')}
              , so {reading.added!.length === 1 ? 'it is' : 'they are'} suggested below and saved with it.
            </p>
          )}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={save}
              disabled={!reading}
              onChange={(e) => setSave(e.target.checked)}
            />
            {reading?.source === 'profile'
              ? 'Save changes to this mapping, or rename it'
              : 'Save this mapping, so the next file laid out like this arrives mapped'}
          </label>
          {save && reading && (
            <label>
              Mapping name
              <input value={saveAs} onChange={(e) => setSaveAs(e.target.value)} />
              {reading.source === 'profile' && (
                <span className="clause">A new name renames it for every file with these column headings.</span>
              )}
            </label>
          )}
        </div>
        )}
        {busy && !reading && <p className="loading">Reading…</p>}
      </div>

      {/* The outcome, under the file it is about. */}
      <div ref={outcome} tabIndex={-1} className="load-outcome">
        {problems.length > 0 ? (
          <div className="load-alert" role="alert">
            <p className="load-alert-title">
              The file was not loaded. Correct {problems.length === 1 ? 'this' : `these ${problems.length}`} in
              the export, then load it again:
            </p>
            <ul className="snapshot-problems" data-testid="problems">
              {problems.map((problem, i) => (
                <li key={i}>{problem}</li>
              ))}
            </ul>
          </div>
        ) : error ? (
          <div className="load-alert" role="alert">
            <p className="load-alert-title">{error}</p>
          </div>
        ) : null}
        {loaded && (
          <div className="load-done" data-testid="loaded" role="status">
            {loaded.length === 1 ? (
              <p className="done">
                Loaded {loaded[0]!.position_count} positions for {loaded[0]!.portfolio_code} as at{' '}
                {asAt(loaded[0]!.as_of)}.
              </p>
            ) : (
              // A file holding several funds: one line for the file, and a row per fund,
              // rather than a sentence per fund run together into a paragraph.
              <>
                <p className="done">
                  Loaded {loaded.length} snapshots,{' '}
                  {loaded.reduce((sum, s) => sum + s.position_count, 0).toLocaleString()} positions, as at{' '}
                  {[...new Set(loaded.map((s) => asAt(s.as_of)))].join(', ')}.
                </p>
                <div className="table-scroll">
                  <table className="grid" data-testid="loaded-funds">
                    <thead>
                      <tr>
                        <th>Fund</th>
                        <th className="num">Positions</th>
                        <th className="num">Market value</th>
                        <th className="num">NAV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loaded.map((s) => (
                        <tr key={s.id}>
                          <td>{s.portfolio_code}</td>
                          <td className="num">{s.position_count}</td>
                          <td className="num">
                            {amount(s.total_market_value)} {s.base_currency}
                          </td>
                          <td className="num">{amount(s.figures.nav)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="actions">
              <button type="button" className="ghost" onClick={startAgain}>
                Load another file
              </button>
              <button type="button" className="ghost" onClick={onLoaded}>
                See the snapshots
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Only while there is something left to decide: once the file has loaded,
          the fund, the date and the columns are on the snapshot, not on this screen. */}
      {reading && file && !loaded && (
        <>
          {/* Which fund and which day first: the context the columns are read in,
              and the inputs loading most often waits for, above a long table that
              a saved mapping has usually already filled in. */}
          <div className="card">
            <h3>Which fund, and when</h3>
            <div className="filters">
              {fundColumn ? (
                <p className="note">
                  Each row names its fund in “{columns.portfolio_code}”, so the file becomes a
                  snapshot for every fund it holds.
                </p>
              ) : (
                <FundPicker
                  label="Fund"
                  value={fund}
                  onChange={setFund}
                  funds={funds}
                  testId="snapshot-fund"
                />
              )}
              {asOfColumn ? (
                <p className="note">The as-of date is read from “{columns.as_of}”.</p>
              ) : (
                <label>
                  As at
                  <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                </label>
              )}
            </div>
            {(() => {
              const fromName = [
                !fundColumn && prefilled.fund && fund === prefilled.fund
                  ? reading.from_filename.portfolio_code
                  : null,
                !asOfColumn && prefilled.asOf && asOf === prefilled.asOf ? prefilled.asOf : null,
              ].filter(Boolean)
              return fromName.length > 0 ? (
                <p className="note" data-testid="from-filename">
                  Filled in from the file name “{file.name}”: {fromName.join(' and ')}. Check{' '}
                  {fromName.length === 1 ? 'it' : 'both'} before loading.
                </p>
              ) : null
            })()}
            {!fundColumn && !figuresMapped && (
              <>
                <p className="note">
                  The fund’s own figures, as its administrator states them. They are what a
                  limit on NAV or net assets divides by, so they are stated here rather than
                  added up from the positions, which leave out accruals and liabilities.
                </p>
                <div className="filters">
                  {Object.entries(FIGURE_LABELS).map(([code, label]) => (
                    <label key={code}>
                      {label}
                      <input
                        inputMode="decimal"
                        value={figures[code] ?? ''}
                        onChange={(e) => setFigures({ ...figures, [code]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="card">
            <h3>What each column holds</h3>
            <p className="note">
              {reading.source === 'profile'
                ? 'Mapped as saved for this layout.'
                : 'Suggested from the column names. Check each one against its sample values.'}{' '}
              {reading.rows_read} position row(s) below row {reading.header_row + 1}
              {reading.declared_count !== null
                ? `, and the file says it holds ${reading.declared_count}`
                : ''}
              .
            </p>
            <div className="filters">
              {reading.sheets.length > 1 && (
                <label>
                  Sheet
                  <select value={reading.sheet} onChange={(e) => void readFile(file, e.target.value)}>
                    {reading.sheets.map((sheet) => (
                      <option key={sheet} value={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Header row
                <input
                  type="number"
                  min={1}
                  value={headerRow}
                  onChange={(e) => setHeaderRow(Number(e.target.value))}
                />
              </label>
              <button
                type="button"
                className="ghost"
                disabled={busy || headerRow - 1 === reading.header_row || headerRow < 1}
                onClick={() => void readFile(file, reading.sheet, headerRow - 1)}
              >
                Read from this row
              </button>
              {/* Asked only when no date in the file can decide it. It sits with the
                  columns, since it says how their dates are read. */}
              {datesUndecided && (
                <label>
                  Dates in the file
                  <select value={dateOrder} onChange={(e) => setDateOrder(e.target.value)}>
                    <option value="">Day or month first?</option>
                    <option value="dmy">Day first, 12/09/2026</option>
                    <option value="mdy">Month first, 09/12/2026</option>
                  </select>
                </label>
              )}
            </div>
            {detected && (
              <p className="note" data-testid="date-order">
                Dates are read {detected === 'dmy' ? 'day first' : 'month first'}:{' '}
                {dayProof ?? monthProof} can only be read that way.
                {reading.date_order && reading.date_order !== detected && (
                  <>
                    {' '}
                    The saved mapping read them{' '}
                    {reading.date_order === 'dmy' ? 'day first' : 'month first'}, so check whether
                    the export has changed.
                  </>
                )}
              </p>
            )}
            {datesConflict && (
              <p className="error" data-testid="date-order">
                This file writes dates both ways: {dayProof} can only be read day first, and{' '}
                {monthProof} can only be read month first. Correct the export, then load it again.
              </p>
            )}
            {datesUndecided && (
              <p className="note" data-testid="date-order">
                No date in the file can only be read one way, so say which way it writes them.
              </p>
            )}
            {!reading.confident && <p className="note">{reading.reason}</p>}
            <div className="table-scroll">
              <table className="grid mapping" data-testid="mapping">
                <thead>
                  <tr>
                    <th>Column in the file</th>
                    <th>Sample values from the snapshot</th>
                    <th>Holds</th>
                  </tr>
                </thead>
                <tbody>
                  {reading.headers.map((header, i) =>
                    header ? (
                      <tr key={`${i}:${header}`}>
                        <td>{header}</td>
                        <td className="sample">
                          {reading.preview
                            .slice(0, 3)
                            .map((row) => row[i] ?? '')
                            .filter(Boolean)
                            .join(' · ')}
                        </td>
                        <td>
                          <select
                            aria-label={`What “${header}” holds`}
                            value={targetOf(header)}
                            onChange={(e) => assign(header, e.target.value)}
                          >
                            <option value="">Not used</option>
                            {TARGET_GROUPS.map((group) => {
                              const inGroup = reading.targets.filter((t) => t.group === group.id)
                              return inGroup.length === 0 ? null : (
                                <optgroup key={group.id} label={group.label}>
                                  {inGroup.map((t) => (
                                    <option key={t.target} value={t.target}>
                                      {t.label}
                                      {t.required ? ' (needed)' : ''}
                                    </option>
                                  ))}
                                </optgroup>
                              )
                            })}
                          </select>
                        </td>
                      </tr>
                    ) : null,
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* In view however far down the mapping somebody has scrolled. */}
      {!loaded && (
        <div className="load-actions">
          <button type="button" disabled={!ready || busy} onClick={() => void submit()}>
            {busy && reading ? 'Loading…' : 'Load the snapshot'}
          </button>
          <span className="note" data-testid="load-waiting">
            {waitingFor.length > 0 ? `To load, ${waitingFor.join(', ')}.` : 'Ready to load.'}
          </span>
        </div>
      )}
    </section>
  )
}
